/**
 * Expense Categories Settings (UI-14 v3 layout, BE-5a data)
 *
 * Route: /settings/expense-categories
 * Categories with their group; groups are managed in the Groups dialog; seed defaults. No delete in the backend —
 * a category or group is retired by switching it inactive. System categories (fines, bank charges) are locked.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Switch, FormControlLabel, Chip, Snackbar, Tooltip, IconButton,
} from '@mui/material';
import { Add, Edit, Lock, AutoAwesome, Category as GroupsIcon } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, StatusChip } from '../../components/accounting';
import { expenseService } from '../../services/accounting/expense.service';
import { serverMessage } from '../../utils/apiConvert';

const yesChipSx = { bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e', fontSize: '0.7rem', height: 20 };
const labelSx = { '& .MuiTypography-root': { fontSize: fs.sm } };

const ExpenseCategories = () => {
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', groupId: '', isSalary: false, active: true });
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [groupEdit, setGroupEdit] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', active: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, grps] = await Promise.all([expenseService.getCategories(), expenseService.getGroups()]);
      setCategories(cats);
      setGroups(grps);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load categories'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const guard = async (fn, done) => {
    setBusy(true);
    setError(null);
    try {
      const result = await fn();
      setSnackbar(typeof result === 'string' ? result : done);
      await load();
      return true;
    } catch (err) {
      setError(serverMessage(err, 'Save failed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const seed = () => guard(async () => {
    const r = await expenseService.seedDefaultCategories();
    return `Default categories: ${r?.created ?? 0} created, ${r?.skipped ?? 0} already there`;
  });

  const handleAdd = () => {
    setForm({ name: '', groupId: groups.find((g) => g.active !== false)?.id || '', isSalary: false, active: true });
    setEditItem({});
  };
  const handleEdit = (c) => {
    setForm({ name: c.name, groupId: c.group?.id || '', isSalary: !!c.isSalary, active: c.active !== false });
    setEditItem(c);
  };
  const handleSave = async () => {
    const data = { name: form.name.trim(), groupId: form.groupId, isSalary: form.isSalary, active: form.active };
    const ok = await guard(() => (editItem?.id ? expenseService.updateCategory(editItem.id, data) : expenseService.createCategory(data)), 'Category saved');
    if (ok) setEditItem(null);
  };

  const openGroup = (g) => { setGroupForm(g ? { name: g.name, active: g.active !== false } : { name: '', active: true }); setGroupEdit(g || {}); };
  const saveGroup = async () => {
    const data = { name: groupForm.name.trim(), active: groupForm.active };
    const ok = await guard(() => (groupEdit?.id ? expenseService.updateGroup(groupEdit.id, data) : expenseService.createGroup(data)), 'Group saved');
    if (ok) setGroupEdit(null);
  };

  const COLUMNS = [
    { key: 'name', label: 'Category Name', width: '34%', render: (r) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {r.systemKey && <Tooltip title="System category — used by fines / bank charges, cannot be edited"><Lock sx={{ fontSize: 14, color: 'text.secondary' }} /></Tooltip>}
        <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium }}>{r.name}</Typography>
        {r.isSalary && <Chip label="Salary" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />}
      </Box>
    ) },
    { key: 'group', label: 'Group', width: '22%', render: (r) => r.group?.name || '—' },
    { key: 'isDefault', label: 'Default', width: '12%', render: (r) => (r.isDefault
      ? <Chip label="Yes" size="small" sx={yesChipSx} />
      : <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>No</Typography>) },
    { key: 'active', label: 'Status', width: '14%', render: (r) => <StatusChip tone={r.active !== false ? 'success' : 'default'} label={r.active !== false ? 'Active' : 'Inactive'} /> },
    { key: 'actions', label: '', width: '8%', align: 'right', render: (r) => (
      <Tooltip title={r.systemKey ? 'System category' : 'Edit'}>
        <span>
          <IconButton size="small" disabled={!!r.systemKey} onClick={() => handleEdit(r)}><Edit sx={{ fontSize: 16 }} /></IconButton>
        </span>
      </Tooltip>
    ) },
  ];
  const GROUP_COLUMNS = [
    { key: 'name', label: 'Group', width: '50%' },
    { key: 'categoryCount', label: 'Categories', width: '20%', align: 'center' },
    { key: 'active', label: 'Status', width: '20%', render: (r) => <StatusChip tone={r.active !== false ? 'success' : 'default'} label={r.active !== false ? 'Active' : 'Inactive'} /> },
    { key: 'actions', label: '', width: '10%', align: 'right', render: (r) => (
      <IconButton size="small" onClick={() => openGroup(r)}><Edit sx={{ fontSize: 16 }} /></IconButton>
    ) },
  ];

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={600} color="secondary.main">Expense Categories</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<AutoAwesome />} onClick={seed} disabled={busy} sx={{ textTransform: 'none' }}>Seed Defaults</Button>
          <Button size="small" startIcon={<GroupsIcon />} onClick={() => setGroupsOpen(true)} sx={{ textTransform: 'none' }}>Groups</Button>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAdd} disabled={groups.length === 0} sx={{ textTransform: 'none' }}>
            Add Category
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {!loading && categories.length === 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          No categories yet. &quot;Seed Defaults&quot; adds the standard set (rent, salaries, utilities, bank charges, fines …) with their GL accounts.
        </Alert>
      )}

      <DataTable columns={COLUMNS} data={categories} loading={loading} emptyMessage="No expense categories configured." />

      <Dialog open={Boolean(editItem)} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>{editItem?.id ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Category Name" size="small" value={form.name} placeholder="e.g. Vehicle Maintenance"
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            <TextField label="Group" size="small" select value={form.groupId} onChange={(e) => setForm((p) => ({ ...p, groupId: e.target.value }))}>
              {groups.filter((g) => g.active !== false || g.id === form.groupId).map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </TextField>
            <FormControlLabel label="Salary category (payee is a team member)" sx={labelSx}
              control={<Switch size="small" checked={form.isSalary} onChange={(e) => setForm((p) => ({ ...p, isSalary: e.target.checked }))} />} />
            <FormControlLabel label="Active" sx={labelSx}
              control={<Switch size="small" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />} />
            {!editItem?.id && (
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>A GL expense account is created for the category automatically.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setEditItem(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" size="small" onClick={handleSave} disabled={busy || !form.name.trim() || !form.groupId} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupsOpen} onClose={() => setGroupsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.sm, fontWeight: fw.semibold, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Expense Groups
          <Button size="small" startIcon={<Add />} onClick={() => openGroup(null)} sx={{ textTransform: 'none' }}>Add Group</Button>
        </DialogTitle>
        <DialogContent>
          <DataTable columns={GROUP_COLUMNS} data={groups} loading={loading} pagination={false} emptyMessage="No groups yet — Seed Defaults adds them." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setGroupsOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(groupEdit)} onClose={() => setGroupEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>{groupEdit?.id ? 'Edit Group' : 'Add Group'}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Group Name" size="small" value={groupForm.name} onChange={(e) => setGroupForm((p) => ({ ...p, name: e.target.value }))} />
            <FormControlLabel label="Active" sx={labelSx}
              control={<Switch size="small" checked={groupForm.active} onChange={(e) => setGroupForm((p) => ({ ...p, active: e.target.checked }))} />} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setGroupEdit(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" size="small" onClick={saveGroup} disabled={busy || !groupForm.name.trim()} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3500} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default ExpenseCategories;
